(() => {
  const PHYSICS = {
    stiffness: 175,
    damping: 26.5,
    mass: 1,
    wheelSensitivity: 0.0048,
    wheelVelocity: 0.018,
    dragSensitivity: 0.02,
    momentumProjection: 0.08,
    settleDelay: 110,
  };

  const normalize = (value, count) => ((value % count) + count) % count;

  const signedDistance = (index, position, count) => {
    const half = count / 2;
    return normalize(index - position + half, count) - half;
  };

  const initialize = (scope = document) => {
    const roots = scope.querySelectorAll("[data-namski-home]:not([data-namski-ready])");

    roots.forEach((root) => {
      root.setAttribute("data-namski-ready", "true");
      const dial = root.querySelector("[data-namski-dial]");
      const items = [...root.querySelectorAll("[data-namski-item]")];
      const backgrounds = [...root.querySelectorAll("[data-namski-background]")];
      const count = items.length;
      if (!dial || count === 0) return;

      let position = 0;
      let target = 0;
      let velocity = 0;
      let activeIndex = 0;
      let frame = 0;
      let previousTime = performance.now();
      let settleTimer = 0;
      let dragging = false;
      let pointerY = 0;
      let pointerTime = 0;
      let pointerTravel = 0;
      let pressedLink = null;
      let suppressClickUntil = 0;
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      const setActive = (index) => {
        const next = normalize(index, count);
        if (next === activeIndex) return;
        activeIndex = next;
        items.forEach((item, itemIndex) => {
          if (itemIndex === activeIndex) item.setAttribute("aria-current", "true");
          else item.removeAttribute("aria-current");
        });
        backgrounds.forEach((background, backgroundIndex) => {
          background.classList.toggle("is-active", backgroundIndex === activeIndex);
        });
      };

      const updateActive = () => {
        const candidate = normalize(Math.round(position), count);
        if (candidate === activeIndex) return;
        const candidateDistance = Math.abs(signedDistance(candidate, position, count));
        const activeDistance = Math.abs(signedDistance(activeIndex, position, count));
        if (candidateDistance < 0.35 && candidateDistance < activeDistance) {
          setActive(candidate);
        }
      };

      const render = () => {
        items.forEach((item, index) => {
          const distance = signedDistance(index, position, count);
          const absolute = Math.abs(distance);
          const hidden = absolute > 2.6;
          const angle = distance * 0.47;
          const y = Math.sin(angle) * 232;
          const z = (Math.cos(angle) - 1) * 83;
          const rotateX = (-angle * 180) / Math.PI;
          const scale = Math.max(0.35, 1 - absolute * 0.33);
          const opacity = hidden ? 0 : Math.max(0.08, 1 - absolute * 0.33);

          item.style.opacity = opacity;
          item.style.zIndex = Math.round(20 - absolute * 4);
          item.style.pointerEvents = hidden ? "none" : "auto";
          item.style.transform = `translate3d(-50%, calc(-50% + ${y}px), ${z}px) rotateX(${rotateX}deg) scale(${scale})`;
        });

        updateActive();
      };

      const animate = (now) => {
        const deltaTime = Math.min((now - previousTime) / 1000, 0.034);
        previousTime = now;

        if (!dragging) {
          const displacement = target - position;
          const acceleration =
            (displacement * PHYSICS.stiffness - velocity * PHYSICS.damping) /
            PHYSICS.mass;
          velocity += acceleration * deltaTime;
          position += velocity * deltaTime;
        }

        render();

        const displacement = target - position;
        if (!dragging && Math.abs(displacement) < 0.0008 && Math.abs(velocity) < 0.008) {
          position = target;
          velocity = 0;
          render();
          frame = 0;
          return;
        }

        frame = requestAnimationFrame(animate);
      };

      const start = () => {
        if (frame) return;
        previousTime = performance.now();
        frame = requestAnimationFrame(animate);
      };

      const settle = () => {
        window.clearTimeout(settleTimer);
        settleTimer = window.setTimeout(() => {
          target = Math.round(position + velocity * PHYSICS.momentumProjection);
          dragging = false;
          start();
        }, PHYSICS.settleDelay);
      };

      dial.addEventListener(
        "wheel",
        (event) => {
          event.preventDefault();
          const delta = Math.max(-90, Math.min(90, event.deltaY));

          if (reducedMotion) {
            position = Math.round(position + Math.sign(delta));
            target = position;
            velocity = 0;
            render();
            return;
          }

          dragging = true;
          position += delta * PHYSICS.wheelSensitivity;
          target = position;
          velocity = delta * PHYSICS.wheelVelocity;
          render();
          settle();
        },
        { passive: false },
      );

      dial.addEventListener("pointerdown", (event) => {
        window.clearTimeout(settleTimer);
        dragging = true;
        pointerY = event.clientY;
        pointerTime = performance.now();
        pointerTravel = 0;
        pressedLink = event.target.closest("[data-namski-item]");
        velocity = 0;
        dial.setPointerCapture(event.pointerId);
      });

      dial.addEventListener("pointermove", (event) => {
        if (!dragging || !dial.hasPointerCapture(event.pointerId)) return;
        const now = performance.now();
        const deltaY = pointerY - event.clientY;
        const deltaTime = Math.max(now - pointerTime, 8);
        const step = deltaY * PHYSICS.dragSensitivity;
        position += step;
        target = position;
        velocity = (step / deltaTime) * 1000;
        pointerY = event.clientY;
        pointerTime = now;
        pointerTravel += Math.abs(deltaY);
        render();
      });

      const endDrag = (event) => {
        if (!dragging) return;
        if (dial.hasPointerCapture(event.pointerId)) dial.releasePointerCapture(event.pointerId);
        const destination = pressedLink?.getAttribute("href");
        const shouldNavigate =
          event.type === "pointerup" &&
          pointerTravel < 5 &&
          destination &&
          destination !== "#";
        pressedLink = null;

        if (shouldNavigate) {
          dragging = false;
          window.location.assign(destination);
          return;
        }

        if (pointerTravel >= 5) suppressClickUntil = performance.now() + 250;

        if (reducedMotion) {
          position = Math.round(position);
          target = position;
          velocity = 0;
          dragging = false;
          render();
          return;
        }

        target = Math.round(position + velocity * PHYSICS.momentumProjection);
        dragging = false;
        start();
      };

      dial.addEventListener("pointerup", endDrag);
      dial.addEventListener("pointercancel", endDrag);

      dial.addEventListener("keydown", (event) => {
        if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
        event.preventDefault();
        target = Math.round(target) + (event.key === "ArrowDown" ? 1 : -1);
        velocity = 0;
        dragging = false;

        if (reducedMotion) {
          position = target;
          render();
          return;
        }

        start();
      });

      items.forEach((item, index) => {
        item.addEventListener("click", (event) => {
          if (performance.now() < suppressClickUntil) {
            event.preventDefault();
            return;
          }

          const destination = item.getAttribute("href");
          if (destination && destination !== "#") return;

          const distance = signedDistance(index, position, count);
          event.preventDefault();
          target = Math.round(position + distance);
          velocity = 0;
          dragging = false;

          if (reducedMotion) {
            position = target;
            render();
            return;
          }

          start();
        });
      });

      render();
    });
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize());
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
})();
